import React, {
  createContext,
  useReducer,
  useContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";

import Engage                      from "../services/engage";
import useConversationSubscription from "../hooks/useConversationSubscription";
import NumbersContext              from "./NumbersContext";

import {
  initConversation,
  conversationReducer,
} from "../reducers/conversationReducer";

import useSentryError              from "../hooks/useSentryError";
import { isDevelopment }           from '../constants/app';

const ConversationContext = createContext(null);

export function ConversationProvider({ id, children }) {
  const { activeNumber } = useContext(NumbersContext);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useSentryError(null);

  const [conversation, dispatch] = useReducer(
    conversationReducer,
    { id, twilio_number: activeNumber },
    initConversation
  );

  const {
    participant,
    customer,
    twilio_number,
    interactions = [],
    pendingInteractions,
  } = conversation || {};

  /**
   *
   * getConversation(id)
   *
   * @param id String HashId of conversation to fetch
   * @return {} Conversation Object
   */
  const getConversation = useCallback(
    async (id) => {
      if (loading) {
        return conversation;
      }

      if (!id && !conversation?.id && !participant?.phone_number) {
        return conversation;
      }

      setLoading(true);

      const conversation_id = id || conversation?.id;

      const res = await Engage.getConversation({
        id: conversation_id,
      });

      if (res.error) {
        setError(res.message);
        dispatch({ type: "CLEAR_CONVERSATION" });
      } else {
        dispatch({ type: "UPDATE_CONVERSATION", payload: res.response });
      }
      setLoading(false);

      return res.response;
    },
    [participant, conversation, loading, setLoading, setError, dispatch]
  );

  /**
   *
   * getConversationByParticipant(id)
   *
   * @param id:String HashId of conversation to fetch
   * @return {} Conversation Object
   */
  const getConversationByParticipant = useCallback(
    async ({ participant_id, remote_id, phone_number }) => {
      if (loading) {
        return conversation;
      }

      setLoading(true);

      const res = await Engage.getConversationByParticipant({
        id: participant_id,
        remote_id: remote_id,
        phone: phone_number,
      });

      if (res.error) {
        // dispatch({ type: "CLEAR_CONVERSATION" });
      } else {
        dispatch({ type: "SET_CONVERSATION", payload: res.response });
      }
      setLoading(false);

      return res.response;
    },
    [loading, setLoading, setError, dispatch]
  );

  /**
   *
   * setConversation(conversation)
   *
   * @param conversation:Object Object representing a conversation with at least an `id` property
   * @return Promise
   */
  const setConversation = useCallback(
    async (payload) => {
      if (payload?.id) {
        dispatch({ type: "SET_CONVERSATION", payload });
      } else {
        setError("An id is required to set conversation", payload);
      }
    },
    [dispatch, setError]
  );

  /**
   * setConversationById(id)
   * Sets conversation by hash ID and optionally fetches conversation
   *
   * @param id:String HashId of a conversation
   * @param shouldFetch:Boolean Flag to determine whether or not to fetch conversation [default=false]
   * @return Promise
   */
  const setConversationById = useCallback(
    async (id, shouldFetch = false) => {
      if (!id) {
        return;
      }
      dispatch({ type: "SET_CONVERSATION", payload: { id } });
      if (shouldFetch) {
        return await getConversation(id);
      }
    },
    [dispatch, getConversation]
  );

  /**
   * openConversation(convo)
   * Sets conversation and marks it as read
   *
   * @param convo:Object Object representing a conversation
   * @param shouldFetch:Boolean Flag to determine whether or not to fetch conversation [default=false]
   * @return Promise
   */
  const openConversation = useCallback(
    async (payload, shouldFetch = false) => {
      if (!payload?.id) {
        setError("Conversation requires an id to be opened.", payload);
        return;
      }
      if (!payload.read) {
        Engage.markConversationAsRead(payload.id);
      }
      dispatch({ type: "SET_CONVERSATION", payload });
      if (shouldFetch) {
        return await getConversation(id);
      }
    },
    [dispatch, setError, getConversation]
  );

  /**
   *
   * clearConversation()
   * Clears conversation data
   *
   * @return
   */
  const clearConversation = useCallback(() => {
    dispatch({ type: "CLEAR_CONVERSATION" });
  }, [dispatch]);

  /**
   *
   * setParticipant(prtcpnt)
   * Sets the participant for the conversation
   *
   * @param prtcpnt:Object Object representing a participant
   * @return
   */
  const setParticipant = useCallback(
    (prtcpnt) => {
      // If participant hasn't changed... skip!
      // if (prtcpnt.phone_number === participant?.phone_number) return false;

      dispatch({
        type: "SET_PARTICIPANT",
        payload: {
          participant: prtcpnt,
          twilio_number: activeNumber,
        },
      });
    },
    [conversation, participant, activeNumber, dispatch]
  );

  /**
   *
   * setParticipantFromCustomer(customer, optionalNumber)
   * Sets the participant for the conversation using a customer record from FieldPulse API
   *
   * @param customer:Object Object representing a customer from the Fieldpulse API
   * @param optionalNumber:String - Used when the selected number isn't the default `phone` property
   * @return
   */
  const setParticipantFromCustomer = useCallback(
    async (customer, optionalNumber) => {
      // if (!customer?.id) return false;

      // Fail safe in case a participant record is sent
      if(customer.remote_id){
        setParticipant(customer)
        return;
      }

      if (customer.id) {
        // Optimistically update conversation with participant
        dispatch({
          type: "SET_CUSTOMER",
          payload: {
            customer: {
              ...customer,
              phone: optionalNumber || customer.phone,
            },
            twilio_number: activeNumber,
          },
        });
      }
      // Create or update existing participant with customer data
      const res = await Engage.createParticipant({
        first_name: customer.first_name,
        last_name: customer.last_name,
        email: customer.email,
        phone_number: optionalNumber || customer.phone,
        remote_id: customer.id,
      })

      // Update conversation with participant response
      if (!res.error) {
        setParticipant(res.response);
      }

      return res.response;
    },
    [activeNumber, setParticipant, dispatch]
  );

  /**
   *
   * onNewInteractionHandler(interaction)
   * Handles incoming "new-message" events from pusher
   *
   * @param interaction Object representing a new interaction
   * @return
   */
  const onNewInteractionHandler = useCallback(
    (interaction) => {
      if(isDevelopment){
        console.log("[ConversationContext:onNewInteractionHandler:interaction]", interaction)
      }
      if(interaction && interaction.conversation_id == conversation?.id){
        dispatch({ type: "APPEND_INTERACTIONS", payload: interaction });
      }else{
        console.error("[ConversationContext:onNewInteractionHandler:INVALID_INTERACTION]", {interaction,conversation})
      }
    },
    [dispatch, conversation]
  );

  // Subscribes to events for the current conversation
  const { socket_id } = useConversationSubscription({
    conversation,
    onNewInteractionHandler
  });

  /**
   *
   * sendMessage(message)
   * Sends a new outgoing text message to API
   *
   * @param message:Object Object representing a new TwilioMessage interaction
   * @return
   */
  const sendMessage = useCallback(
    async (message) => {
      if (!twilio_number?.phone_number) {
        setError(
          "A twilio number is required to send a text message.",
          message
        );
        return;
      }
      if (!participant?.phone_number) {
        setError(
          "An outgoing number is required to send a text message.",
          message
        );
        return;
      }
      if (!message?.text) {
        setError("A message is required to send a text message.", message);
        return;
      }

      setSending(true);

      const _id = Math.round(Math.random() * 1000000);

      const interaction = {
        ...message,
        from: twilio_number.phone_number,
        to: participant.phone_number,
        pending: true,
        _id,
      };

      dispatch({ type: "APPEND_PENDING_INTERACTION", payload: interaction });

      const res = await Engage.sendMessage(interaction, socket_id);

      if (res.error) {
        setError(res.message, res);
      } else {
        dispatch({
          type: "UPDATE_PENDING_INTERACTION",
          payload: {
            ...interaction,
            ...res.response,
            pending: false,
            _id,
          },
        });
      }
      setSending(false);
      return res;
    },
    [twilio_number, participant, conversation, socket_id, setSending, dispatch]
  );

  // A memoized array that combines pending and existing interactions
  const allInteractions = useMemo(
    () =>
      interactions
        ? [...interactions, ...pendingInteractions]
        : [...pendingInteractions],
    [interactions, pendingInteractions]
  );

  useEffect(() => {
    if (!conversation?.id && participant?.id) {
      getConversationByParticipant({
        participant_id: participant?.id,
        remote_id: participant.remote_id,
        phone_number: participant?.phone_number || customer?.phone,
      });
    }
  }, [participant?.id, conversation.id]);

  return (
    <ConversationContext.Provider
      value={{
        loading,
        sending,
        error,
        conversation: {
          ...conversation,
          interactions: allInteractions,
        },
        getConversation,
        getConversationByParticipant,
        openConversation,
        setConversation,
        setConversationById,
        clearConversation,
        setParticipantFromCustomer,
        setParticipant,
        sendMessage,
      }}
    >
      {children}
    </ConversationContext.Provider>
  );
}

export default ConversationContext;
